import { useEffect } from 'react';
// Relative, not '@/': the alias is configured for webpack but not for jest,
// and applyPageMeta below is unit-tested.
import { canonicalUrl, SITE_URL } from '../data/pageMeta';

/**
 * Apply a page's metadata to the document and return a cleanup function that
 * restores the previous state, so navigating between routes never leaves stale
 * tags behind.
 *
 * Tags already present in public/index.html (description, og:*, twitter:*) are
 * updated in place and restored. Tags we introduce (canonical, robots) are
 * created and then removed.
 *
 * React 19 can hoist <title>/<meta>/<link> rendered inside a component, which
 * would make this unnecessary. It is done explicitly because that behavior
 * cannot be verified without a browser, whereas this function is unit-tested.
 *
 * Exported separately from the hook so it can be tested without rendering React.
 */
export function applyPageMeta(meta) {
  if (!meta || !meta.title) return () => {};

  const { title, description, ogImage } = meta;
  const noindex = Boolean(meta.noindex);
  const canonical = canonicalUrl(meta);

  const previousTitle = document.title;
  document.title = title;

  // Each entry records an element and its content before we touched it.
  // previous === null means we created it and must remove it on cleanup.
  const touched = [];

  const setMeta = (selectorAttr, attrValue, content) => {
    let el = document.querySelector(`meta[${selectorAttr}="${attrValue}"]`);
    if (el) {
      touched.push({ el, previous: el.getAttribute('content') });
    } else {
      el = document.createElement('meta');
      el.setAttribute(selectorAttr, attrValue);
      document.head.appendChild(el);
      touched.push({ el, previous: null });
    }
    el.setAttribute('content', content);
  };

  if (description) {
    setMeta('name', 'description', description);
    setMeta('property', 'og:description', description);
    setMeta('name', 'twitter:description', description);
  }
  setMeta('property', 'og:title', title);
  setMeta('name', 'twitter:title', title);

  // A page may carry its own share card. Absolute, because the Open Graph spec
  // requires it and relative URLs are simply not fetched by most scrapers.
  // Pages without one keep the site-wide card from index.html.
  if (ogImage) {
    const absolute = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;
    setMeta('property', 'og:image', absolute);
    setMeta('name', 'twitter:image', absolute);
  }

  let canonicalEl = null;
  let previousCanonicalHref = null;
  if (canonical) {
    canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) {
      previousCanonicalHref = canonicalEl.getAttribute('href');
    } else {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);
    setMeta('property', 'og:url', canonical);
  }

  let robotsEl = null;
  let previousRobotsContent = null;
  if (noindex) {
    robotsEl = document.querySelector('meta[name="robots"]');
    if (robotsEl) {
      previousRobotsContent = robotsEl.getAttribute('content');
    } else {
      robotsEl = document.createElement('meta');
      robotsEl.setAttribute('name', 'robots');
      document.head.appendChild(robotsEl);
    }
    robotsEl.setAttribute('content', 'noindex, follow');
  }

  return function restore() {
    document.title = previousTitle;

    touched.forEach(({ el, previous }) => {
      if (previous === null) el.remove();
      else el.setAttribute('content', previous);
    });

    if (canonicalEl) {
      if (previousCanonicalHref === null) canonicalEl.remove();
      else canonicalEl.setAttribute('href', previousCanonicalHref);
    }

    if (robotsEl) {
      if (previousRobotsContent === null) robotsEl.remove();
      else robotsEl.setAttribute('content', previousRobotsContent);
    }
  };
}

/** React binding: applies metadata on mount, restores it on unmount. */
export default function usePageMeta(meta) {
  const { title, description, path, noindex, ogImage } = meta || {};

  useEffect(
    () => applyPageMeta({ title, description, path, noindex, ogImage }),
    [title, description, path, noindex, ogImage],
  );
}
